import React from "react";
import { useTranslation } from "react-i18next";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";

import styles from "./BookingFilters.module.scss";
import { bookableRates } from "./coachMeta";
import PillDropdown from "./PillDropdown";

export interface BookingFilterState {
  coachId: string;
  /**
   * What the facility calendar draws: `"sessions"` its coaches' bookable times,
   * `"clinics"` the clinics and events running that day, `"all"` both.
   *
   * It is not a filter over session *titles*, which is what it started as. A
   * facility's day holds two different things and the athlete arrives wanting one
   * of them; twenty-four titles in a dropdown was never that choice.
   */
  sessionType: string;
  duration: string;
}

/** The two kinds of thing a facility's day holds. */
export const SESSION_TYPE_SESSIONS = "sessions";
export const SESSION_TYPE_CLINICS = "clinics";

/**
 * The filters a screen starts with: none of them.
 *
 * Duration used to open on 60 minutes, on the reasoning that an hour is the
 * common session and an open filter listed 15 minutes beside 3 hours. It was
 * the wrong trade. An athlete arriving on a date or on the Facility Calendar
 * was shown a filtered day without having asked for one, and the options the
 * facility actually had at 30 or 90 minutes were simply missing — including,
 * for a coach who offers no hour at all, that coach. A filter the athlete did
 * not set should not be deciding what they are allowed to see.
 */
export const EMPTY_FILTERS: BookingFilterState = {
  coachId: "all",
  sessionType: "all",
  duration: "all",
};

export const hasAnyFilter = (filters: BookingFilterState) =>
  filters.coachId !== "all" ||
  filters.sessionType !== "all" ||
  filters.duration !== "all";

interface Props {
  coaches: CoachProfileModel[];
  filters: BookingFilterState;
  onChange: (filters: BookingFilterState) => void;
  /**
   * Session Type belongs to the Facility Calendar, the one screen that draws both
   * sessions and clinics and so the only one where choosing between them means
   * anything. The Dates tab lists booking options; there is nothing there for it
   * to hide.
   */
  showSessionType?: boolean;
}

/**
 * The second row of the facility booking page: All, Session Type, Coach,
 * Duration. Shared by the Dates and Facility Calendar tabs, which the frame
 * draws with the same row.
 *
 * The options come from the facility's own rates, so a facility only ever offers
 * filters it can actually honour.
 */
const BookingFilters: React.FC<Props> = ({
  coaches,
  filters,
  onChange,
  showSessionType = false,
}) => {
  const { t } = useTranslation();

  const rates = coaches.flatMap((coach) => bookableRates(coach));
  const durations = Array.from(
    new Set(rates.map((rate) => rate.durationInMinutes)),
  ).sort((a, b) => a - b);

  const set = (patch: Partial<BookingFilterState>) =>
    onChange({ ...filters, ...patch });

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={hasAnyFilter(filters) ? styles.chip_off : styles.chip_on}
        onClick={() => onChange(EMPTY_FILTERS)}
        data-testid="booking-filter-all"
      >
        {t("All")}
      </button>

      {showSessionType && (
        <PillDropdown
          label={t("Session Type")}
          value={filters.sessionType}
          options={[
            { value: SESSION_TYPE_SESSIONS, label: t("Sessions") },
            { value: SESSION_TYPE_CLINICS, label: t("Clinics & Events") },
          ]}
          onChange={(sessionType) => set({ sessionType })}
          testId="booking-filter-session-type"
        />
      )}

      <PillDropdown
        label={t("Coach")}
        value={filters.coachId}
        options={coaches.map((coach) => ({
          value: coach.id,
          label: coach.fullName,
        }))}
        onChange={(coachId) => set({ coachId })}
        testId="booking-filter-coach"
      />

      <PillDropdown
        label={t("Duration")}
        value={filters.duration}
        options={durations.map((minutes) => ({
          value: String(minutes),
          label: t("{{count}} mins", { count: minutes }),
        }))}
        onChange={(duration) => set({ duration })}
        testId="booking-filter-duration"
      />
    </div>
  );
};

export default BookingFilters;
